import type { ActionCreator, StateFromReducersMapObject } from "redux"
import { apiRequestsSlice } from "./api/apiRequestsSlice"
import apiSlice from "./api/apiSlice"
import echoSlice from "./Echo.Slice"
import clientReducer from "./client/reducer"
import extractsReducer from "./extracts/reducer"
import integrationsReducer from "./integrations/reducer"
import patientsReducer from "./patients/reducer"
import professionalsReducer from "./professionals/reducer"
import { configureStore } from "@reduxjs/toolkit/react"
import { applyTenantedApiRequestMiddleware } from "../middleware/ApiRequestMiddleware"
import {
	applyLastOneWinsMiddleware,
	applyLastOneWinsValidationMiddleware,
	combineReducersWithRequestValidation
} from "../middleware/RequestValidationMiddleware"
import { apiMiddleware } from "redux-api-middleware"
import environmentSlice from "./environmentSlice"
import { zoneRegistry } from "mco-zone-configurations"
import DeepPartial from "mco-typescript-core/src/DeepPartial"
import patientsSlice from "./patientsSlice"
import phinExceptionsSlice from "./phinExceptionsSlice"
import genesisCareIntegrationSlice from "./Integrations.GenesisCare.Slice"
import searchSlice from "./searchSlice"
import { useDispatch, useSelector } from "react-redux"
import { useCallback } from "react"

const reducer = {
	[apiSlice.reducerPath]: apiSlice.reducer,
	[echoSlice.reducerPath]: echoSlice.reducer,
	[apiRequestsSlice.reducerPath]: apiRequestsSlice.reducer,
	[environmentSlice.reducerPath]: environmentSlice.reducer,
	[patientsSlice.reducerPath]: patientsSlice.reducer,
	[phinExceptionsSlice.reducerPath]: phinExceptionsSlice.reducer,
	[searchSlice.reducerPath]: searchSlice.reducer,
	[genesisCareIntegrationSlice.reducerPath]: genesisCareIntegrationSlice.reducer,

	// TODO: WABA: Migrate the following to RTK
	client: clientReducer,
	extracts: extractsReducer,
	integrations: integrationsReducer,
	patients: patientsReducer,
	professionals: professionalsReducer
}

export type State = StateFromReducersMapObject<typeof reducer>

const loadState = () => {
	try {
		const serializedState = localStorage.getItem("state")
		if (serializedState === null) {
			return undefined
		}
		const savedState = JSON.parse(serializedState)
		return savedState as State
	} catch {
		return undefined
	}
}

const saveState = (state: DeepPartial<State>) => {
	try {
		const stateToSave = {
			environment: state.environment
		}

		const serializedState = JSON.stringify(stateToSave)
		localStorage.setItem("state", serializedState)
	} catch {
		// ignore write errors
	}
}

const initialState = loadState()

const store = configureStore({
	preloadedState: initialState,
	reducer: combineReducersWithRequestValidation(reducer),
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware().concat(
			apiSlice.middleware,
			echoSlice.middleware,
			applyTenantedApiRequestMiddleware,
			applyLastOneWinsValidationMiddleware,
			apiMiddleware,
			applyLastOneWinsMiddleware
		)
})

export const useStoreSelector = useSelector.withTypes<State>()

export type StoreDispatch = typeof store.dispatch
export const useStoreDispatch = useDispatch.withTypes<StoreDispatch>()

export const useActionDispatch = <T extends ActionCreator<any>>(
	actionCreator: T
) => {
	const dispatch = useStoreDispatch()
	const dispatcher = useCallback(
		(...args: Parameters<typeof actionCreator>) => {
			dispatch(actionCreator(...args))
		},
		[actionCreator, dispatch]
	)
	return dispatcher
}

store.subscribe(() => {
	saveState(store.getState())
})

if (!zoneRegistry.isProduction()) {
	const win = window as any
	win.mco = win.mco || {}
	win.mco.store = store
}

export default store
